import { useCallback } from 'react'

import { getAmountExcludingVat } from '@features/invoices/utils/getAmountExcludingVat'
import { getAmountIncludingVat } from '@features/invoices/utils/getAmountIncludingVat'
import { ProductPricingModel } from '@features/products/enums/productPricingModel'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useFormContext } from '../../../hooks'
import { ProductEditorFormSchema } from '../types/productEditorFormSchema'

export const usePriceCalculations = () => {
  const { organization } = useUserOrganization()
  const { setValue, watch, trigger } = useFormContext<ProductEditorFormSchema>()
  const [price, total, vatRate, pricingModel] = watch(['price', 'total', 'vatRate', 'pricingModel'])

  const handlePriceChange = useCallback(
    (price?: number) => {
      if (price === undefined) {
        return
      }

      if (organization?.isVatExempted) {
        setValue('total', price)
        return
      }

      setValue('total', getAmountIncludingVat(price, vatRate?.rate))
      setValue('pricingModel', ProductPricingModel.Price)
      trigger('total')
    },
    [organization?.isVatExempted, setValue, trigger, vatRate?.rate],
  )

  const handleTotalChange = useCallback(
    (total?: number) => {
      if (total === undefined) {
        return
      }
      setValue('price', getAmountExcludingVat(total, vatRate?.rate))
      setValue('pricingModel', ProductPricingModel.Total)
      trigger('price')
    },
    [setValue, trigger, vatRate?.rate],
  )

  const handleVatRateChange = useCallback(
    (selectedVatRate?: number) => {
      if (selectedVatRate === undefined) {
        return
      }
      if (pricingModel === ProductPricingModel.Price && price !== undefined) {
        setValue('total', getAmountIncludingVat(price, selectedVatRate))
        trigger('total')
      } else if (pricingModel === ProductPricingModel.Total && total !== undefined) {
        setValue('price', getAmountExcludingVat(total, selectedVatRate))
        trigger('price')
      }
    },
    [price, pricingModel, setValue, total, trigger],
  )

  return {
    handlePriceChange,
    handleTotalChange,
    handleVatRateChange,
  }
}
